//changed due to package upgrade
import React, { useEffect, useRef, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Divider,
  FormHelperText,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import IntlMessages from '@projectK/helpers/IntlMessages';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context';
import AppInfoView from '@projectK/components/AppInfoView';
import AppGridContainer from '@projectK/components/AppGridContainer';
import AppTextField from '@projectK/components/AppTextField';
import EditIcon from '@mui/icons-material/Edit';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import ReactQuill from 'react-quill-new';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

const validationSchema = yup.object({
  contact_email: yup
    .string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      'Invalid email format.',
    )
    .typeError(<IntlMessages id='validation.invalidEmail' />)
    .required(<IntlMessages id='validation.emailRequired' />),
  buffer_days: yup
    .number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === '' ? null : value,
    )
    .nullable()
    .typeError('Grace Period must be a number.')
    .min(0, 'Grace Period cannot be negative.'),
});

const getButtonBackgroundColor = (companyTheme, isMargin) => ({
  ...(isMargin && { mt: 6 }), // Conditionally add mt only if isMargin is true
  backgroundColor: companyTheme ? companyTheme.primary.main : '#2196f3',
  '&:hover': {
    backgroundColor: companyTheme
      ? alpha(companyTheme.primary.main, 0.8)
      : alpha('#2196f3', 0.8),
  },
});
const getTextColor = (companyTheme) => ({
  color: companyTheme ? companyTheme.primary.main : '#2196f3',
});
const getBorderColor = (companyTheme) => ({
  borderColor: companyTheme ? companyTheme.primary.main : '#2196f3',
  '&:hover': {
    borderColor: companyTheme
      ? alpha(companyTheme.primary.main, 0.8)
      : alpha('#2196f3', 0.8),
  },
  color: companyTheme ? companyTheme.primary.main : '#2196f3',
});
const MissedPaymentSetting = () => {//NOSONAR
  const [settings, setSettings] = useState(null);
  const infoViewActionsContext = useInfoViewActionsContext();
  const [isEdit, setIsEdit] = useState(false);
  const companyTheme = CompanyTheme();
  const editorRef = useRef(null);
  const convertToJSDate = (date) => {
    return date ? dayjs(date).toDate() : null;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await jwtAxios.get(`/payments-recovery-settings`);
      setSettings(response.data);
    } catch (error) {
      // Handle error
      console.error('Error fetching payments recovery settings:', error);
    }
  };
  return (
    <>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Formik
          enableReinitialize={true}
          validateOnBlur={true}
          initialValues={{
            contact_email: settings?.contact_email || '',
            missed_date_from: settings?.missed_date_from
              ? `${settings.missed_date_from.substring(0, 10)}T12:01:00`
              : null,
            note: settings?.note || '',
            buffer_days: settings?.buffer_days || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            // Prepare the data for submission

            const requestData = {
              contact_email:
                values.contact_email === '' ? null : values.contact_email,
              missed_date_from:
                values.missed_date_from === '' ? null : values.missed_date_from,
              note: values.note === '' ? null : values.note,
              buffer_days:
                values.buffer_days === '' ? null : parseInt(values.buffer_days),
            };
            jwtAxios
              .put(`/payments-recovery-settings`, requestData)
              .then((response) => {
                setSubmitting(false);
                infoViewActionsContext.showMessage(
                  'Settings Updated Successfully!',
                );
                setIsEdit(false);
                fetchData();
              })
              .catch((error) => {
                infoViewActionsContext.fetchError(error.message);
                console.error('Error updating basic settings:', error);
                setSubmitting(false);
              });
          }}
        >
          {(formikProps) => (
            <Form noValidate autoComplete='off'>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    variant='h4'
                    sx={(theme) => getTextColor(companyTheme)}
                  >
                    <IntlMessages id='sidebar.admin.paymentRecovery' />
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ mt: 2, color: 'text.secondary' }}
                  >
                    Manage restrictions for users who have missed payments.
                  </Typography>
                </Box>
                <Box>
                  {!isEdit && (
                    <EditIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setIsEdit(true)}
                    />
                  )}
                </Box>
              </Box>
              <Divider sx={{ my: 4 }} />
              <AppGridContainer>
                <Grid size={{ xs: 12, md: 12 }}>
                  <Box
                    sx={{
                      border: '1px solid #EAECF0',
                      p: 6,
                      borderRadius: 3,
                      boxShadow:
                        '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                    }}
                  >
                    <AppGridContainer>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <AppTextField
                          name='contact_email'
                          variant='outlined'
                          placeholder='Enter support or billing email'
                          sx={{
                            width: '100%',
                          }}
                          label='Contact Email'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <Stack spacing={1}>
                            <DatePicker
                              label='Start Checking Missed Payments From'
                              value={convertToJSDate(
                                formikProps.values?.missed_date_from,
                              )}
                              disabled={!isEdit}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = dayjs(newValue)
                                    .startOf('day')
                                    .format('YYYY-MM-DD');
                                  formikProps.setFieldValue(
                                    'missed_date_from',
                                    formattedDate !== 'Invalid Date'
                                      ? formattedDate
                                      : null,
                                  );
                                } else {
                                  formikProps.setFieldValue(
                                    'missed_date_from',
                                    null,
                                  );
                                }
                              }}
                              name='missed_date_from'
                              onError={(newError) =>
                                formikProps.setErrors({
                                  missed_date_from: newError,
                                })
                              }
                              slotProps={{
                                textField: {
                                  readOnly: !isEdit, // ✅ this is the correct way
                                },
                              }}
                            />
                            <FormHelperText sx={{ color: '#d32f2f', ml: 2 }}>
                              {formikProps.errors.missed_date_from}
                            </FormHelperText>
                          </Stack>
                        </LocalizationProvider>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <AppTextField
                          name='buffer_days'
                          variant='outlined'
                          placeholder='Enter grace period in days'
                          sx={{ width: '100%' }}
                          label='Grace Period (in Days)'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography
                          variant='h4'
                          style={{ marginBottom: '10px' }}
                        >
                          Restriction Message
                        </Typography>
                        <ReactQuill
                          className='desc-box'
                          ref={editorRef}
                          value={formikProps.values.note}
                          onChange={(content, editor) => {
                            const cleanContent = content.trim();
                            if (cleanContent === '') {
                              formikProps.setFieldValue('note', '');
                            } else {
                              formikProps.setFieldValue('note', content);
                            }
                          }}
                          readOnly={!isEdit}
                          onBlur={(range, source, quill) => {
                            formikProps.handleBlur({
                              target: { name: 'note' },
                            });
                          }}
                          modules={{
                            toolbar: [
                              [{ header: '1' }, { header: '2' }, { font: [] }],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              ['bold', 'italic', 'underline', 'strike'],
                              [{ align: [] }],
                              ['link'],
                            ],
                          }}
                          formats={[
                            'header',
                            'font',
                            'list',
                            'bold',
                            'italic',
                            'underline',
                            'strike',
                            'align',
                            'link',
                          ]}
                          // Adjusted styles
                        />
                        {formikProps.touched.note &&
                          formikProps.errors.note && (
                            <div
                              className='error'
                              style={{
                                color: '#d32f2f',
                                fontSize: '13px',
                                marginLeft: '13px',
                                marginTop: '6px',
                              }}
                            >
                              {formikProps.errors.note}
                            </div>
                          )}
                        <Typography
                          variant='body3'
                          sx={{ mb: 2, color: 'text.secondary' }}
                        >
                          This message will be displayed to users who are
                          restricted due to missed payments.
                        </Typography>
                      </Grid>
                    </AppGridContainer>
                  </Box>
                </Grid>
              </AppGridContainer>
              {isEdit && (
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  spacing={5}
                  sx={{ mt: 3 }}
                >
                  <Button
                    getBorderColor
                    sx={(theme) => getBorderColor(companyTheme)}
                    onClick={() => {
                      setIsEdit(false);
                      formikProps.setErrors({});
                      formikProps.resetForm();
                    }}
                  >
                    <IntlMessages id='common.cancel' />
                  </Button>
                  <Button
                    variant='contained'
                    color='primary'
                    type='submit'
                    sx={(theme) =>
                      getButtonBackgroundColor(companyTheme, false)
                    }
                    disabled={formikProps.isSubmitting}
                  >
                    <IntlMessages id='common.save' />
                  </Button>
                </Stack>
              )}
            </Form>
          )}
        </Formik>
      </Box>
      <AppInfoView />
    </>
  );
};
export default MissedPaymentSetting;
